class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;
    this._onVisualViewportChange = this._onVisualViewportChange.bind(this);
    this.syncResultsA11yState(false);

    this.setupEventListeners();
  }

  connectedCallback() {
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this._onVisualViewportChange);
      window.visualViewport.addEventListener("scroll", this._onVisualViewportChange);
    }
  }

  disconnectedCallback() {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this._onVisualViewportChange);
      window.visualViewport.removeEventListener("scroll", this._onVisualViewportChange);
    }
  }

  _onVisualViewportChange() {
    if (!this.getAttribute("open") || !this.predictiveSearchResults) return;
    if (this.closest(".header-search-modal")) return;
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  syncResultsA11yState(isOpen) {
    if (!this.predictiveSearchResults) return;

    // Keep the results out of SR navigation and tab order until the listbox is actually open.
    this.predictiveSearchResults.hidden = !isOpen;
    this.predictiveSearchResults.inert = !isOpen;
    this.predictiveSearchResults.setAttribute("aria-hidden", isOpen ? "false" : "true");
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    if (event.code !== "ArrowUp" && event.code !== "ArrowDown" && event.code !== "Enter") {
      return;
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
      return;
    }

    if (
      !this.getAttribute("open") ||
      !this.predictiveSearchResults ||
      event.target !== this.input
    ) {
      return;
    }

    const panel = this.predictiveSearchResults;
    const pageStep = Math.round(panel.clientHeight * 0.65) || 120;

    if (event.code === "PageDown") {
      event.preventDefault();
      panel.scrollTop += pageStep;
    } else if (event.code === "PageUp") {
      event.preventDefault();
      panel.scrollTop -= pageStep;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);

    this.scrollOptionIntoResultsPanel(activeElement);
  }

  /**
   * scrollIntoView({ block: "nearest" }) often does not move nested overflow scrollers.
   * Adjust the results panel's scrollTop so the active option stays visible.
   */
  scrollOptionIntoResultsPanel(optionEl) {
    const panel = this.predictiveSearchResults;
    if (!panel || !optionEl) return;

    const run = () => {
      const margin = 12;
      const pr = panel.getBoundingClientRect();
      const er = optionEl.getBoundingClientRect();

      if (er.top < pr.top + margin) {
        panel.scrollTop += er.top - pr.top - margin;
      } else if (er.bottom > pr.bottom - margin) {
        panel.scrollTop += er.bottom - pr.bottom + margin;
      }
    };

    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }

  /** When the panel extends below the visual viewport (zoom / short windows), scroll the document. */
  scrollPageIfPanelBelowFold() {
    if (this.closest(".header-search-modal")) return;
    const panel = this.predictiveSearchResults;
    if (!panel || !window.visualViewport) return;

    requestAnimationFrame(() => {
      const vv = window.visualViewport;
      const pr = panel.getBoundingClientRect();
      const vvBottom = vv.offsetTop + vv.height;
      const overflow = pr.bottom - vvBottom + 24;
      if (overflow > 0) {
        window.scrollBy({ top: overflow, left: 0, behavior: "auto" });
      }
    });
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    fetch(
      `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    const vv = window.visualViewport;
    const vh = vv?.height ?? window.innerHeight;
    const rect = this.input?.getBoundingClientRect();
    if (!rect) {
      return Math.round(Math.min(vh * 0.88, 480));
    }

    const gutter = 16;
    const bottomEdge = vv ? vv.offsetTop + vv.height : vh;
    let spaceBelow = bottomEdge - rect.bottom - gutter;

    const maxPanel = vh * 0.92;

    if (spaceBelow < 100) {
      spaceBelow = Math.min(maxPanel, vh * 0.42);
    } else {
      spaceBelow = Math.max(0, spaceBelow);
    }

    return Math.round(Math.min(maxPanel, spaceBelow));
  }

  open() {
    const inSearchModal = this.closest(".header-search-modal");
    if (inSearchModal) {
      this.predictiveSearchResults.style.maxHeight = "";
    } else {
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
      this.predictiveSearchResults.scrollTop = 0;
    }
    this.syncResultsA11yState(true);
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;

    this.scrollPageIfPanelBelowFold();
    requestAnimationFrame(() => {
      const id = this.input.getAttribute("aria-activedescendant");
      if (id) {
        const el = this.querySelector(`#${CSS.escape(id)}`);
        if (el) this.scrollOptionIntoResultsPanel(el);
      }
    });
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.predictiveSearchResults.removeAttribute("style");
    this.syncResultsA11yState(false);

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
